import { lazy } from "react";
import { ReceiptText } from "lucide-react";

import type { FeatureDescriptor } from "@/features/types";

/**
 * Billing is two workspaces under one nav entry, not one page — validated
 * against Stripe (Subscriptions vs Invoices), Zuora (Bill Runs vs Invoice
 * Management), SAP IS-U (Invoicing vs FICA) and Oracle Utilities CC&B/C2M
 * (bill run vs bill/account inquiry): every reference system keeps
 * *generating* documents and *managing* already-generated ones on separate
 * screens, even though both sit under one "Billing" area. Reasons that hold
 * here too: different frequency/risk (a periodic, high-stakes trigger vs
 * daily operational work), different data shape (a handful of periods vs
 * thousands of bill rows needing locality/status/aging filters), and
 * different state machines (a period's open/billed/closed lifecycle vs a
 * bill's draft/issued/paid/overdue one).
 *
 * `/billing` (`BillsPage`) is the primary, daily-use surface — browsing and
 * acting on bills, the thing operators do far more often than generating a
 * period. `/billing/runs` (`BillingRunsPage`) is the periodic generation
 * workspace. This mirrors the `sensorsFeature` shape below (`/sensors` +
 * `/sensors/registry`, linked via an in-page button, not a second nav item)
 * rather than inventing a new pattern for billing alone.
 */
export const billingFeature: FeatureDescriptor = {
  id: "billing",
  flag: "billing",
  path: "/billing",
  group: "analyse",
  navKey: "nav.billing",
  icon: ReceiptText,
  roles: ["admin", "manager"], // mirrors the backend billing guards
  // Locality-only, no day-range: a bill belongs to one locality regardless of
  // when it's viewed, and periods (not a rolling window) are billing's own
  // time dimension — same shape as Leaks/Sensor Registry (see
  // [[global-scope-filtering]]).
  scope: { locality: true, days: false },
  element: lazy(() => import("./BillsPage")),
  subRoutes: [
    // Periods/generation carry no locality dimension (a period covers every
    // locality at once) — no scope declared, so ScopeBar hides its controls
    // here exactly like the other utility-style sub-routes in this app.
    { path: "/billing/runs", element: lazy(() => import("./BillingRunsPage")) },
  ],
};
